/**
 * @file
 * Fixed header: toolbar offset, measured height for layout spacer + scroll-padding,
 * scrolled shadow, and auto-hide on scroll down / show on scroll up (all viewports).
 */

/** @param {HTMLElement} root */
function zkSetAdminOffsetVars(root, offsetPx) {
  const v = offsetPx > 0 ? `${offsetPx}px` : '0px';
  root.style.setProperty('--zk-toolbar-offset', v);
  root.style.setProperty('--zk-admin-offset', v);
}

/**
 * Vertical space (px) reserved under fixed/sticky admin toolbars at the top.
 *
 * @param {Window & typeof globalThis} win
 */
function zkGetToolbarOffset(win) {
  const doc = win.document;
  const candidates = [
    '#gin-toolbar-bar',
    '.gin-toolbar-bar',
    '#toolbar-bar',
    '.toolbar-bar',
    '.gin-secondary-toolbar--frontend',
  ];

  let maxBottom = 0;
  candidates.forEach((sel) => {
    const el = doc.querySelector(sel);
    if (!el || el.closest('[hidden]')) {
      return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.height < 1) {
      return;
    }
    if (rect.top < -5 || rect.top > 140) {
      return;
    }
    maxBottom = Math.max(maxBottom, Math.round(rect.bottom));
  });

  return Math.min(maxBottom, 240);
}

/**
 * Header spacer is defined in CSS (--zk-header-visible-height). Runtime remeasure
 * caused CLS when JS overwrote the token after first paint.
 */

/**
 * @param {Window & typeof globalThis} win
 */
function zkInitHeaderScroll(win) {
  const doc = win.document;
  const body = doc.body;
  const header = doc.querySelector('.zk-header');
  const elevator = doc.querySelector('.zk-header__elevator');
  if (!header || !elevator || !body) {
    return;
  }

  const panel = doc.getElementById('zkMainNav');
  const mqReduce = win.matchMedia('(prefers-reduced-motion: reduce)');

  let ticking = false;
  let lastScrollY = win.scrollY;
  let cachedToolbarOffset = zkGetToolbarOffset(win);

  const scrollThreshold = 96;
  const deltaThreshold = 8;

  const isMenuOpen = () =>
    Boolean(panel?.classList.contains('show') || body.classList.contains('zk-menu-open'));

  /** Toolbar height does not change while scrolling — remeasure only on resize/UI. */
  const applyToolbarOffset = () => {
    cachedToolbarOffset = zkGetToolbarOffset(win);
    zkSetAdminOffsetVars(doc.documentElement, cachedToolbarOffset);
  };

  const applyScrollState = () => {
    const y = win.scrollY;
    const delta = y - lastScrollY;
    const menuOpen = isMenuOpen();
    const reduceMotion = mqReduce.matches;

    const scrolled = y > 20;
    body.classList.toggle('is-header-scrolled', scrolled);
    header.classList.toggle('is-header-scrolled', scrolled);
    header.classList.toggle('zk-header--scrolled', scrolled);

    if (reduceMotion || menuOpen || y <= 10) {
      body.classList.remove('is-header-hidden');
      body.classList.add('is-header-visible');
      body.classList.remove('is-scrolling-up', 'is-scrolling-down');
      lastScrollY = y;
      return;
    }

    if (delta < -deltaThreshold) {
      body.classList.remove('is-header-hidden');
      body.classList.add('is-header-visible');
      body.classList.add('is-scrolling-up');
      body.classList.remove('is-scrolling-down');
    }
    else if (delta > deltaThreshold && y > scrollThreshold) {
      body.classList.add('is-header-hidden');
      body.classList.remove('is-header-visible');
      body.classList.add('is-scrolling-down');
      body.classList.remove('is-scrolling-up');
    }

    lastScrollY = y;
  };

  const onScroll = () => {
    if (ticking) {
      return;
    }
    ticking = true;
    win.requestAnimationFrame(() => {
      ticking = false;
      applyScrollState();
    });
  };

  const onResize = () => {
    applyToolbarOffset();
    applyScrollState();
  };

  applyToolbarOffset();
  body.classList.add('is-header-visible');
  applyScrollState();

  win.addEventListener('scroll', onScroll, { passive: true });
  win.addEventListener('resize', onResize, { passive: true });

  if (panel) {
    ['show.bs.offcanvas', 'shown.bs.offcanvas', 'hide.bs.offcanvas', 'hidden.bs.offcanvas'].forEach((ev) => {
      panel.addEventListener(ev, () => {
        onResize();
      });
    });

    if (typeof MutationObserver !== 'undefined') {
      const obs = new MutationObserver(() => {
        onResize();
      });
      obs.observe(panel, { attributes: true, attributeFilter: ['class'] });
    }
  }

  if (typeof mqReduce.addEventListener === 'function') {
    mqReduce.addEventListener('change', onResize);
  }
  else if (typeof mqReduce.addListener === 'function') {
    mqReduce.addListener(onResize);
  }

  doc.documentElement.classList.add('zk-header-scroll-initialized');
}

/**
 * @param {Window & typeof globalThis} win
 */
export function registerZkHeaderScrollBehavior(win) {
  const Drupal = win.Drupal;
  const onceFn = win.once;
  if (!Drupal || typeof onceFn !== 'function') {
    return;
  }

  Drupal.behaviors.zkHeaderScroll = {
    attach(context) {
      onceFn('zk-header-scroll-init', 'html', context).forEach(() => {
        zkInitHeaderScroll(win);
      });
    },
  };
}
